import React, { useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDAlert from "../../components/MDAlert";
import MDTypography from "../../components/MDTypography";
import { AuthContext } from "../../context";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import GenericForm from "../genericForm";
import apiService from "../../services/api-service";

function UserExternalAdd() {
	const authContext = useContext(AuthContext);
	const navigate = useNavigate();
	const isSupplierUser = authContext.hasAuth("ADD_SUPPLIER_EMPLOYEES");
	const [error] = useState("");
	const [suppliersOption, setSuppliersOption] = useState([]);
	const [hasLogin, setHasLogin] = useState(true);
	const [initialData] = useState(!isSupplierUser ? { has_login: true } : {});

	const fetchData = useCallback(() => {
		// Solo gli admin hanno bisogno della lista fornitori
		if (!authContext.hasAuth("ADD_SUPPLIER_EMPLOYEES")) {
			apiService
				.get("/suppliers-management/", {}, {})
				.then((data) => {
					console.log("[UserExternalAdd] Suppliers loaded:", data);
					setSuppliersOption(data);
					if (data && data.length === 1) {
						console.log("[UserExternalAdd] Single supplier detected, will auto-select:", data[0]);
					}
				})
				.catch((error) => console.log("[UserExternalAdd] Error loading suppliers:", error));
		}
	}, [authContext]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Campi diversi per fornitori vs admin
	const inputFields = useMemo(
		() =>
			isSupplierUser
				? [
						// Form per fornitori (con email per login)
						{ type: "text", name: "first_name", label: "Nome", required: true },
						{ type: "text", name: "last_name", label: "Cognome", required: true },
						{ type: "text", name: "fiscal_code", label: "Codice Fiscale", required: true },
						{ type: "date", name: "date_of_birth", label: "Data di Nascita", required: true },
						{ type: "email", name: "login_email", label: "Email", required: false },
						{ type: "text", name: "phone", label: "Telefono", required: false },
					]
				: [
						// Form completo per admin
						{ type: "text", name: "first_name", label: "Nome", required: true },
						{ type: "text", name: "last_name", label: "Cognome", required: true },
						{
							type: "checkbox",
							name: "has_login",
							label: "L'utente può fare login?",
							required: false,
						},
						{ type: "text", name: "fiscal_code", label: "Codice Fiscale", required: hasLogin },
						...(hasLogin
							? [
									{ type: "text", name: "email", label: "Email", required: true },
									{ type: "text", name: "phone", label: "Telefono", required: true },
									{ type: "text", name: "password", label: "Password", required: true },
								]
							: []),
						{
							type: "autocomplete",
							name: "supplier",
							label: "Fornitore",
							options: suppliersOption,
							valueToDisplay: (value, options) => {
								if (typeof value === "object") {
									return `${value.name}`;
								}
								const option = options.find((opt) => opt.id === value);
								return option ? `${option.name}` : "fornitore non trovato";
							},
							valueToSave: (selected) => {
								return selected?.id;
							},
							valueComparator: (option, value) => {
								if (typeof value === "number") {
									return option.id === value;
								}
								return option.id === value.id;
							},
						},
						{
							type: "checkbox",
							name: "is_admin",
							label: "È un amministratore del fornitore?",
							required: false,
						},
					],
		[isSupplierUser, hasLogin, suppliersOption]
	);

	const requiredFields = isSupplierUser
		? [
				// Campi richiesti per fornitori
				{ name: "first_name", nameDiplayed: "Nome" },
				{ name: "last_name", nameDiplayed: "Cognome" },
				{ name: "fiscal_code", nameDiplayed: "Codice Fiscale" },
				{ name: "date_of_birth", nameDiplayed: "Data di Nascita" },
			]
		: [
				// Campi richiesti per admin
				{ name: "first_name", nameDiplayed: "Nome" },
				{ name: "last_name", nameDiplayed: "Cognome" },
				...(hasLogin
					? [
							{ name: "fiscal_code", nameDiplayed: "Codice Fiscale" },
							{ name: "email", nameDiplayed: "Email" },
						]
					: []),
				{ name: "supplier", nameDiplayed: "Fornitore" },
			];

	console.log("[UserExternalAdd] Component state:", {
		isSupplierUser: isSupplierUser,
		suppliersOption: suppliersOption,
		suppliersCount: suppliersOption.length,
		requiredFields: requiredFields,
	});

	return (
		<DashboardLayout>
			{authContext.hasAuth("ADD_EXTERNAL_USER") || authContext.hasAuth("ADD_SUPPLIER_EMPLOYEES") ? (
				<>
					<DashboardNavbar />
					<MDBox pt={6} pb={3}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => {
								// Se ha permessi fornitore, torna alla pagina fornitori, altrimenti alla pagina admin
								const backPath = authContext.hasAuth("VIEW_SUPPLIER_EMPLOYEES")
									? "/supplier-employees"
									: "/users-external-managment";
								navigate(backPath);
							}}
							sx={{ mt: 2, alignSelf: "center", minWidth: "100px", color: "#ffffff", marginBottom: "30px" }}
						>
							Torna alla tabella
						</Button>
						{error !== "" ? (
							<Alert severity="error">{error}</Alert>
						) : (
							<GenericForm
								authModelName={"EXTERNAL_USER"}
								inputFields={inputFields}
								requiredFields={requiredFields}
								url="/user-external-management/"
								old_data={initialData}
								useAPI={true}
								executeOnChange={true}
								executeOnChangeTask={(formData) => {
									if (formData.has_login !== undefined && formData.has_login !== hasLogin) {
										setHasLogin(formData.has_login);
									}
								}}
								redirectOnSuccess={
									authContext.hasAuth("VIEW_SUPPLIER_EMPLOYEES")
										? "/supplier-employees/edit"
										: "/users-external-managment/edit"
								}
							/>
						)}
					</MDBox>
				</>
			) : (
				<MDAlert color="error" dismissible>
					<MDTypography variant="body2">You don't have permission to access this page</MDTypography>
				</MDAlert>
			)}
		</DashboardLayout>
	);
}

export default UserExternalAdd;
